import JointAccountOwnerApiClient from "@/api/jointAccountOwnerApiClient";
import { JointAccountOwner } from "@/types";
import { AppToasterV2, CoreFieldValue, hashStringSHA256 } from "@ucl/library";
import { AxiosError } from "axios";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";

// Browser-compatible hash function using Web Crypto API

export const useJointAccountOwnerDocuments = (jointOwnerId: string) => {
  const [formValue, setFormValueInternal] = useState<JointAccountOwner | undefined>();
  // Hash of the form value
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const jointAccountOwnerApiClient = useAuthenticatedApiClient(JointAccountOwnerApiClient);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await saveIdentityDocuments(formValue);
      }
    },
    debounceDelay: 1000,
  });

  useEffect(() => {
    getJointAccountOwner();
  }, [jointOwnerId]);

  const getJointAccountOwner = async () => {
    setIsLoading(true);
    const owner = await jointAccountOwnerApiClient.getJointAccountOwnerById(jointOwnerId);
    setFormValueInternal(owner);
    const ownerHash = await hashStringSHA256(JSON.stringify(owner));
    setFormValueHash(ownerHash);
    setIsLoading(false);
  };

  const setFormValue = async (value: JointAccountOwner, newFieldValue: CoreFieldValue) => {
    if (isNil(newFieldValue) || isNil(value.identificationDocuments) || isNil(value)) {
      return;
    }

    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (formValueHash === newHash) {
      return;
    }

    // Run any UI field changes
    setFormValueInternal({ ...value });

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const saveIdentityDocuments = useCallback(
    async (data: JointAccountOwner) => {
      setIsSaving(true);

      try {
        const updated = await jointAccountOwnerApiClient.updateJointAccountOwnerIdentityDocuments(
          data.financialApplicationId || "",
          jointOwnerId,
          data
        );

        return updated;
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving joint account owner data:", error);

        AppToasterV2.showFailedActionToaster("Failed to save joint account owner data.");
      } finally {
        setIsSaving(false);
      }
    },
    [jointOwnerId, jointAccountOwnerApiClient]
  );

  return {
    formValue,
    setFormValue,
    saveIdentityDocuments,
    isSaving,
    isLoading,
  };
};
